/**
 * The scale a series' intensity is read and typed on: the unit a value on
 * it carries, the precision it is shown with, and the conversion to and
 * from the stored intensity. A value is never shown or accepted on a scale
 * its label does not name.
 */
export interface IntensityScale {
    /** The unit a value on this scale carries, or '' for a bare stored value. */
    readonly unit: string;
    /** Decimal places a value on this scale is shown and typed with. */
    readonly decimals: number;
    /** A stored intensity as a value on this scale. */
    toDisplay(stored: number): number;
    /** The stored intensity a value on this scale names; the inverse of {@link IntensityScale.toDisplay}. */
    toStored(display: number): number;
}

/** The stored intensity itself: bare whole numbers, as a CT or MR window is read. */
export const STORED_INTENSITY_SCALE: IntensityScale = {
    unit: '',
    decimals: 0,
    toDisplay: (stored) => stored,
    toStored: (display) => display,
};

/** Tolerance for a value that is a whole number of the scale's steps up to float noise. */
const STEP_TOLERANCE = 1e-9;

/**
 * Whether a typed value has no more decimals than the scale shows, so the
 * value stored is the value typed and the readout repeats it exactly. A
 * finer value is refused rather than rounded out of sight.
 */
export function isOnScale(value: number, scale: IntensityScale): boolean {
    if (!Number.isFinite(value)) {
        return false;
    }
    const steps = value * 10 ** scale.decimals;
    return (
        Math.abs(steps - Math.round(steps)) <=
        STEP_TOLERANCE * Math.max(1, Math.abs(steps))
    );
}

/**
 * A stored intensity as a value on a scale, rounded to the scale's
 * decimals: the number a readout shows and an editor opens with.
 */
export function roundOnScale(scale: IntensityScale, stored: number): number {
    const steps = 10 ** scale.decimals;
    const rounded = Math.round(scale.toDisplay(stored) * steps) / steps;
    // A negative value that rounds away is zero, not negative zero.
    return rounded === 0 ? 0 : rounded;
}

/**
 * A stored intensity as a readout shows it on a scale, so one value never
 * reads as two numbers in two places.
 */
export function formatOnScale(scale: IntensityScale, stored: number): string {
    const rounded = roundOnScale(scale, stored);
    return scale.decimals === 0
        ? String(rounded)
        : rounded.toFixed(scale.decimals);
}
